import { useEffect, useState } from "react";
import { MapPin, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";

const inputCls =
  "w-full h-12 px-4 rounded-xl border border-zinc-200 bg-white text-base placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-950 focus:ring-offset-2 transition-shadow";

export default function Locations() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [locations, setLocations] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/locations").then(({ data }) => setLocations(data));
  useEffect(() => { load(); }, []);

  const openDialog = (loc = null) => {
    setEditing(loc);
    setName(loc?.name || "");
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/locations/${editing.id}`, { name });
        toast.success("Ubicación actualizada");
      } else {
        await api.post("/locations", { name });
        toast.success("Ubicación creada");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (loc) => {
    try {
      await api.delete(`/locations/${loc.id}`);
      toast.success("Ubicación eliminada");
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  return (
    <div className="animate-fade-up" data-testid="locations-page">
      <div className="flex items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tighter">Ubicaciones</h1>
          <p className="text-zinc-500 text-sm mt-1">Organiza tus máquinas por planta, nave o zona.</p>
        </div>
        {isAdmin && (
          <button
            onClick={() => openDialog()}
            data-testid="add-location-button"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-zinc-900 text-white text-sm font-medium hover:bg-zinc-800 active:scale-95 transition-[background-color,transform] duration-200 shrink-0"
          >
            <Plus size={16} /> <span className="hidden sm:inline">Nueva ubicación</span>
          </button>
        )}
      </div>

      {locations === null ? (
        <div className="flex justify-center py-20">
          <div className="h-8 w-8 rounded-full border-2 border-zinc-300 border-t-zinc-900 animate-spin" />
        </div>
      ) : locations.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-3xl border border-dashed border-zinc-200" data-testid="locations-empty">
          <MapPin className="mx-auto text-zinc-300 mb-4" size={36} strokeWidth={1.5} />
          <p className="text-zinc-500">Aún no hay ubicaciones.</p>
          {isAdmin && <p className="text-zinc-400 text-sm mt-1">Crea tu primera ubicación para organizar las máquinas.</p>}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {locations.map((loc) => (
            <div
              key={loc.id}
              data-testid={`location-card-${loc.id}`}
              className="bg-white rounded-2xl border border-zinc-200/80 card-shadow p-5 flex items-center gap-4"
            >
              <span className="h-11 w-11 rounded-xl bg-zinc-100 flex items-center justify-center text-zinc-500 shrink-0">
                <MapPin size={18} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-medium truncate">{loc.name}</p>
                <p className="text-sm text-zinc-400">
                  {loc.machine_count} {loc.machine_count === 1 ? "máquina" : "máquinas"}
                </p>
              </div>
              {isAdmin && (
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => openDialog(loc)}
                    title="Renombrar"
                    data-testid={`location-edit-${loc.id}`}
                    className="h-9 w-9 rounded-full flex items-center justify-center text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
                  >
                    <Pencil size={15} />
                  </button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button
                        title="Eliminar"
                        data-testid={`location-delete-${loc.id}`}
                        className="h-9 w-9 rounded-full flex items-center justify-center text-zinc-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                      >
                        <Trash2 size={15} />
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="rounded-3xl">
                      <AlertDialogHeader>
                        <AlertDialogTitle>¿Eliminar «{loc.name}»?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Las máquinas de esta ubicación no se eliminarán; quedarán sin ubicación asignada.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-full">Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDelete(loc)} className="rounded-full bg-rose-600 hover:bg-rose-700" data-testid="location-delete-confirm">
                          Eliminar
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-3xl sm:max-w-sm" data-testid="location-dialog" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">
              {editing ? "Renombrar ubicación" : "Nueva ubicación"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 pt-2">
            <input
              className={inputCls}
              placeholder='Ej. "Nave 1 - Inyección"'
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
              data-testid="location-name-input"
            />
            <button
              type="submit"
              disabled={saving}
              data-testid="location-submit"
              className="w-full h-12 rounded-full bg-zinc-900 text-white font-medium hover:bg-zinc-800 active:scale-[0.98] transition-[background-color,transform] duration-200 disabled:opacity-50"
            >
              {saving ? "Guardando…" : editing ? "Guardar" : "Crear ubicación"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
